package songwritersnotebook.app.utils

import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import org.w3c.dom.events.Event
import org.w3c.files.Blob
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.await
import songwritersnotebook.app.di.PlatformDependencyProvider
import kotlin.js.Promise
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.withContext

// External declarations for Web Audio API
external class AudioContext {
    val currentTime: Double
    val destination: AudioNode
    fun createBufferSource(): AudioBufferSourceNode
    fun createGain(): GainNode
    fun createOscillator(): OscillatorNode
    fun decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer>
    fun close(): Promise<Unit>
    val state: String
    fun resume(): Promise<Unit>
}

external class OfflineAudioContext(numberOfChannels: Int, length: Int, sampleRate: Int) {
    val destination: AudioNode
    fun createBufferSource(): AudioBufferSourceNode
    fun createGain(): GainNode
    fun createOscillator(): OscillatorNode
    fun decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer>
    fun startRendering(): Promise<AudioBuffer>
}

external class AudioBuffer {
    val duration: Double
    val length: Int
    val numberOfChannels: Int
    val sampleRate: Float
    fun getChannelData(channel: Int): org.khronos.webgl.Float32Array
}

external open class AudioNode {
    fun connect(destination: AudioNode)
    fun disconnect()
}

external class AudioBufferSourceNode : AudioNode {
    var buffer: AudioBuffer?
    var loop: Boolean
    fun start(whenTime: Double = definedExternally, offset: Double = definedExternally, duration: Double = definedExternally)
    fun stop(whenTime: Double = definedExternally)
    var onended: ((Event) -> Unit)?
}

external class OscillatorNode : AudioNode {
    val frequency: AudioParam
    fun start(whenTime: Double = definedExternally)
    fun stop(whenTime: Double = definedExternally)
    var onended: ((Event) -> Unit)?
}

external class GainNode : AudioNode {
    val gain: AudioParam
}

external class AudioParam {
    var value: Float
    fun setValueAtTime(value: Float, time: Double)
    fun linearRampToValueAtTime(value: Float, time: Double)
}

actual class AudioHandler actual constructor() {
    private val audioContext: AudioContext by lazy {
        js("new (window.AudioContext || window.webkitAudioContext)()") as AudioContext
    }

    private val sampleCache = mutableMapOf<String, AudioBuffer>()
    private val trackCache = mutableMapOf<String, AudioBuffer>()
    private val trackVolumes = mutableMapOf<String, Float>()
    private val audioScope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private var currentSource: AudioBufferSourceNode? = null
    
    private var isPlaying = false
    private var isRecording = false
    private var onRecordingSaved: (() -> Unit)? = null

    // Fallback for JS AudioContext API
    private var recorder: Any? = null
    private var recordingChunks = mutableListOf<Blob>()
    private var currentRecordingPath: String? = null

    private val activeSources = mutableMapOf<String, AudioBufferSourceNode>()

    // Global gain for all notes to avoid node creation overhead
    private val masterGain: GainNode by lazy {
        val g = audioContext.createGain()
        g.connect(audioContext.destination)
        g
    }

    fun resume() {
        if (audioContext.state == "suspended") {
            audioContext.resume()
        }
    }

    private fun ensureContextResumed() {
        if (audioContext.state == "suspended") {
            audioContext.resume()
        }
    }

    private suspend fun fetchAudioDataAsync(url: String): ArrayBuffer {
        println("DEBUG_AUDIO_JS: fetchAudioDataAsync called with url: $url")
        val finalUrl = if (url.startsWith("composeResources")) "/$url" else url
        if (finalUrl.startsWith("http") || finalUrl.startsWith("blob:") || finalUrl.startsWith("/")) {
            println("DEBUG_AUDIO_JS: fetchAudioDataAsync executing NetworkUtils.fetchAuthenticated for $finalUrl")
            val bytes = NetworkUtils().fetchAuthenticated(finalUrl)
            println("DEBUG_AUDIO_JS: fetchAudioDataAsync got ${bytes.size} bytes back")
            val uint8Array = org.khronos.webgl.Uint8Array(
                (bytes.asDynamic() as org.khronos.webgl.Int8Array).buffer,
                (bytes.asDynamic() as org.khronos.webgl.Int8Array).byteOffset,
                (bytes.asDynamic() as org.khronos.webgl.Int8Array).length
            )
            val finalBuffer = uint8Array.buffer.slice(uint8Array.byteOffset, uint8Array.byteOffset + uint8Array.byteLength)
            println("DEBUG_AUDIO_JS: fetchAudioDataAsync returning ArrayBuffer of length ${finalBuffer.byteLength}")
            return finalBuffer
        } else {
            // Internal path
            println("DEBUG_AUDIO_JS: fetchAudioDataAsync using local PlatformFileSystem for $url")
            val file = songwritersnotebook.platform.getPlatformFileSystem().getFile(url)
            if (file.exists()) {
                val bytes = file.readBytes()
                println("DEBUG_AUDIO_JS: Local file read ${bytes.size} bytes")
                val uint8Array = org.khronos.webgl.Uint8Array(
                    (bytes.asDynamic() as org.khronos.webgl.Int8Array).buffer,
                    (bytes.asDynamic() as org.khronos.webgl.Int8Array).byteOffset,
                    (bytes.asDynamic() as org.khronos.webgl.Int8Array).length
                )
                return uint8Array.buffer.slice(uint8Array.byteOffset, uint8Array.byteOffset + uint8Array.byteLength)
            } else {
                println("DEBUG_AUDIO_JS: Local file does not exist: $url")
                throw Exception("File not found: $url")
            }
        }
    }

    actual fun startRecording(filePath: String) {
        recordingChunks.clear()
        currentRecordingPath = filePath
        window.navigator.asDynamic().mediaDevices.getUserMedia(js("{audio: true}")).then { stream ->
            // Robust MIME type detection
            val mimeType = listOf("audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4", "audio/aac")
                .firstOrNull { type -> 
                    val isSupported = js("MediaRecorder.isTypeSupported(type)") as Boolean
                    isSupported
                } ?: ""
            
            println("Recording with MIME type: $mimeType")
            val options = js("{}")
            if (mimeType.isNotEmpty()) {
                options.mimeType = mimeType
            }
            
            val rec = SnBridge.createMediaRecorder(stream, options)
            rec.ondataavailable = { event: dynamic ->
                val chunk = event.data as Blob
                recordingChunks.add(chunk)
            }
            rec.onstop = {
                val path = currentRecordingPath
                if (path != null && recordingChunks.isNotEmpty()) {
                    val chunksArray = recordingChunks.toTypedArray()
                    val blobOptions = js("{type: mimeType}")
                    val finalBlob = Blob(chunksArray, blobOptions)
                    val arrayBufferPromise = finalBlob.asDynamic().arrayBuffer() as Promise<ArrayBuffer>
                    arrayBufferPromise.then { buffer ->
                        val int8Array = org.khronos.webgl.Int8Array(buffer)
                        val bytes = int8Array.unsafeCast<ByteArray>()
                        songwritersnotebook.platform.getPlatformFileSystem().getFile(path).writeBytes(bytes)
                        println("Recording saved to memory file: $path (${bytes.size} bytes)")
                        onRecordingSaved?.invoke()
                        onRecordingSaved = null
                    }.catch { err ->
                        println("Error saving recording: $err")
                        onRecordingSaved?.invoke()
                        onRecordingSaved = null
                    }
                } else {
                    onRecordingSaved?.invoke()
                    onRecordingSaved = null
                }
                println("Recording stopped, ${recordingChunks.size} chunks captured.")
            }
            rec.start()
            recorder = rec
        }.catch { err ->
            println("Error starting recording: $err")
        }
    }

    actual suspend fun stopRecording() {
        if (recorder == null) return
        kotlin.coroutines.suspendCoroutine<Unit> { cont ->
            onRecordingSaved = {
                cont.resumeWith(Result.success(Unit))
            }
            stopRecordingNonBlocking()
        }
    }

    actual fun stopRecordingNonBlocking() {
        recorder?.asDynamic()?.stop()
        recorder = null
    }

    actual fun getMaxAmplitude(): Int = Int.MAX_VALUE

    actual fun playAudio(filePath: String, onCompletion: () -> Unit) {
        stopPlayback()
        ensureContextResumed()
        
        audioScope.launch {
            try {
                val buffer = fetchAudioDataAsync(filePath)
                val audioBuffer = audioContext.decodeAudioData(buffer).await()
                
                withContext(Dispatchers.Main) {
                    val source = audioContext.createBufferSource()
                    source.buffer = audioBuffer
                    source.connect(masterGain)
                    source.onended = { 
                        if (currentSource == source) {
                            currentSource = null
                        }
                        source.disconnect()
                        onCompletion() 
                    }
                    source.start(0.0)
                    currentSource = source
                }
            } catch (err: Throwable) {
                println("Error playing audio ($filePath): $err")
                withContext(Dispatchers.Main) { onCompletion() }
            }
        }
    }

    actual fun playNote(note: String, instrument: String, volume: Float) {
        ensureContextResumed()
        val assetPath = "composeResources/songwritersnotebookapp.composeapp.generated.resources/files/$instrument/$note.mp3"
        
        if (sampleCache.containsKey(assetPath)) {
            playBuffer(sampleCache[assetPath]!!, volume)
        } else {
            audioScope.launch {
                try {
                    val buffer = fetchAudioDataAsync(assetPath)
                    val audioBuffer = audioContext.decodeAudioData(buffer).await()
                    sampleCache[assetPath] = audioBuffer
                    withContext(Dispatchers.Main) {
                        playBuffer(audioBuffer, volume)
                    }
                } catch (err: Throwable) {
                    val fallbackPath = "files/$instrument/$note.mp3"
                    try {
                        val buffer = fetchAudioDataAsync(fallbackPath)
                        val ab = audioContext.decodeAudioData(buffer).await()
                        sampleCache[assetPath] = ab
                        withContext(Dispatchers.Main) {
                            playBuffer(ab, volume)
                        }
                    } catch (e: Throwable) {
                        println("AUDIO ERROR: Failed to load $assetPath or $fallbackPath: ${e.message}")
                    }
                }
            }
        }
    }

    private fun playBuffer(buffer: AudioBuffer, volume: Float) {
        val source = audioContext.createBufferSource()
        source.buffer = buffer
        
        // Use a GainNode for precise volume
        val gainNode = audioContext.createGain()
        gainNode.gain.value = volume
        
        source.connect(gainNode)
        gainNode.connect(masterGain)
        
        // Ensure cleanup to prevent node leakage
        source.onended = {
            source.disconnect()
            gainNode.disconnect()
        }
        
        source.start(0.0)
    }

    actual fun stopPlayback() {
        currentSource?.stop()
        currentSource = null
    }

    actual fun discardRecording(filePath: String) {
        try {
            songwritersnotebook.platform.getPlatformFileSystem().getFile(filePath).delete()
            println("Discarded and freed memory for: $filePath")
        } catch (e: Exception) {
            // Ignore if already deleted
        }
    }

    suspend fun loadSample(instrument: String, note: String) {
        val extensions = listOf("mp3", "wav")
        for (ext in extensions) {
            val assetPath = "composeResources/songwritersnotebookapp.composeapp.generated.resources/files/$instrument/$note.$ext"
            if (sampleCache.containsKey(assetPath)) return

            try {
                val buffer = fetchAudioDataAsync(assetPath)
                val audioBuffer = audioContext.decodeAudioData(buffer as ArrayBuffer).await()
                sampleCache[assetPath] = audioBuffer
                return // Success
            } catch (e: Throwable) {
                // Try next extension
            }
        }
    }

    actual fun release() {
        stopPlayback()
        stopAllTrackRecordings()
        audioScope.launch {
            audioContext.close()
        }
    }

    private var metronomeJob: kotlinx.coroutines.Job? = null
    private var isMetronomeMuted = false

    actual fun startMetronome(bpm: Int, onBeat: (Int) -> Unit) {
        stopMetronome()
        
        // Pre-load metronome samples
        audioScope.launch {
            loadSample("metronome", "high")
            loadSample("metronome", "low")
        }

        val intervalMs = (60000.0 / bpm).toLong()
        metronomeJob = audioScope.launch {
            var beat = 0
            while (isActive) {
                if (!isMetronomeMuted) {
                    playMetronomeClick(beat == 0)
                }
                onBeat(beat)
                beat = (beat + 1) % 4
                delay(intervalMs)
            }
        }
    }

    private fun playMetronomeClick(isDownbeat: Boolean) {
        val assetPath = "composeResources/songwritersnotebookapp.composeapp.generated.resources/files/metronome/low.wav"
        val buffer = sampleCache[assetPath]
        if (buffer != null) {
            playBuffer(buffer, 0.5f)
        } else {
            // Fallback to oscillator if samples aren't loaded yet
            ensureContextResumed()
            val osc = audioContext.createOscillator()
            val gain = audioContext.createGain()
            osc.frequency.value = 800f
            gain.gain.value = 0.1f
            osc.connect(gain)
            gain.connect(masterGain)
            osc.onended = {
                osc.disconnect()
                gain.disconnect()
            }
            osc.start(audioContext.currentTime)
            osc.stop(audioContext.currentTime + 0.05)
        }
    }

    actual fun stopMetronome() {
        metronomeJob?.cancel()
        metronomeJob = null
    }

    actual fun setMetronomeMuted(muted: Boolean) {
        isMetronomeMuted = muted
    }

    actual suspend fun cacheRecordingPcm(url: String): ShortArray? = null

    actual fun prepareTrackRecording(
        itemId: String,
        url: String,
        volume: Float,
        startMillis: Int,
        durationMillis: Int,
        preparationDeferred: CompletableDeferred<Unit>
    ) {
        if (url.isBlank()) {
            preparationDeferred.complete(Unit)
            return
        }

        trackVolumes[itemId] = volume

        if (trackCache.containsKey(itemId)) {
            preparationDeferred.complete(Unit)
            return
        }

        audioScope.launch {
            try {
                val buffer = fetchAudioDataAsync(url)
                val audioBuffer = audioContext.decodeAudioData(buffer).await()
                trackCache[itemId] = audioBuffer
                preparationDeferred.complete(Unit)
            } catch (err: Throwable) {
                println("Error preparing track $itemId ($url): $err")
                preparationDeferred.complete(Unit)
            }
        }
    }

    actual fun startPreparedTrack(itemId: String, offsetMillis: Int) {
        val buffer = trackCache[itemId] ?: return
        val volume = trackVolumes[itemId] ?: 1.0f
        ensureContextResumed()

        val source = audioContext.createBufferSource()
        source.buffer = buffer
        
        val gainNode = audioContext.createGain()
        gainNode.gain.value = volume
        
        source.connect(gainNode)
        gainNode.connect(masterGain)
        
        val offsetSeconds = offsetMillis / 1000.0
        if (offsetSeconds < buffer.duration) {
            source.start(0.0, offsetSeconds)
            activeSources[itemId] = source
            source.onended = {
                activeSources.remove(itemId)
                source.disconnect()
                gainNode.disconnect()
            }
        }
    }

    actual fun stopAllTrackRecordings() {
        activeSources.values.forEach { 
            try {
                it.stop()
                it.disconnect()
            } catch (e: Exception) {
                // Ignore errors if already stopped
            }
        }
        activeSources.clear()
    }

    actual suspend fun renderStudioProject(
        tracks: List<songwritersnotebook.app.data.Track>,
        bpm: Int,
        chordLibrary: Map<String, songwritersnotebook.app.data.ChordDefinition>,
        song: songwritersnotebook.app.data.Song?
    ): Any? {
        val totalBeats = tracks.flatMap { it.items }.maxOfOrNull { it.beat + it.duration } ?: 0f
        if (totalBeats <= 0f) {
            println("RENDER: No items found to render.")
            return null
        }

        val sampleRate = 44100
        val bps = bpm / 60.0
        val durationSeconds = totalBeats / bps
        val totalFrames = (durationSeconds * sampleRate).toInt().coerceAtLeast(100)
        
        println("RENDER: Starting offline render. Duration: $durationSeconds s, Frames: $totalFrames, BPM: $bpm")
        
        val offlineCtx = OfflineAudioContext(2, totalFrames, sampleRate)
        val masterGain = offlineCtx.createGain()
        masterGain.connect(offlineCtx.destination)

        var scheduledCount = 0

        tracks.forEach { track ->
            if (track.isMuted) return@forEach
            val anySoloed = tracks.any { it.isSoloed }
            if (anySoloed && !track.isSoloed) return@forEach

            var trackVolume = track.volume
            if (track.name.lowercase().contains("vocal")) trackVolume *= 4.0f

            track.items.forEach { item ->
                val startSeconds = item.beat / bps
                
                when (item.type) {
                    "note" -> {
                        item.data.notes?.forEach { noteName ->
                            val instrument = item.data.instrument ?: "piano"
                            val extensions = listOf("mp3", "wav")
                            var buffer: AudioBuffer? = null
                            for (ext in extensions) {
                                val assetPath = "composeResources/songwritersnotebookapp.composeapp.generated.resources/files/$instrument/$noteName.$ext"
                                buffer = sampleCache[assetPath]
                                if (buffer != null) break
                            }
                            
                            if (buffer == null) {
                                println("RENDER: Missing sample buffer for $noteName in $instrument")
                                return@forEach
                            }
                            val source = offlineCtx.createBufferSource()
                            source.buffer = buffer
                            val g = offlineCtx.createGain()
                            g.gain.value = trackVolume
                            source.connect(g)
                            g.connect(masterGain)
                            source.start(startSeconds)
                            scheduledCount++
                        }
                    }
                    "chord" -> {
                        val chordDef = chordLibrary[item.data.name]
                        if (chordDef == null) {
                            println("RENDER: Missing chord definition for ${item.data.name}")
                            return@forEach
                        }
                        val instrument = item.data.instrument ?: "piano"
                        val notesToPlay = if (instrument.equals("guitar", ignoreCase = true)) {
                            val tab = chordDef.guitarTab
                            if (tab != null && song != null) {
                                val tabForConversion = tab.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }
                                songwritersnotebook.app.utils.ChordUtils.tabToNotes(tabForConversion, song.guitarTuning, song.guitarCapo)
                            } else emptyList()
                        } else chordDef.pianoNotes ?: emptyList()

                        val maxStrumDelayMs = 200L
                        val clampedStrumSpeed = track.strumSpeed.coerceIn(0.05f, 1.0f)
                        val noteDelaySeconds = (maxStrumDelayMs * (1.0f / clampedStrumSpeed - 1.0f) / (notesToPlay.size - 1).coerceAtLeast(1)) / 1000.0

                        notesToPlay.forEachIndexed { index, noteName ->
                            val extensions = listOf("mp3", "wav")
                            var buffer: AudioBuffer? = null
                            for (ext in extensions) {
                                val assetPath = "composeResources/songwritersnotebookapp.composeapp.generated.resources/files/$instrument/$noteName.$ext"
                                buffer = sampleCache[assetPath]
                                if (buffer != null) break
                            }

                            if (buffer == null) {
                                println("RENDER: Missing chord sample buffer for $noteName in $instrument")
                                return@forEach
                            }
                            val source = offlineCtx.createBufferSource()
                            source.buffer = buffer
                            val g = offlineCtx.createGain()
                            g.gain.value = trackVolume
                            source.connect(g)
                            g.connect(masterGain)
                            source.start(startSeconds + (index * noteDelaySeconds))
                            scheduledCount++
                        }
                    }
                    "recording" -> {
                        val buffer = trackCache[item.id]
                        if (buffer == null) {
                            println("RENDER: Missing recording buffer for item ${item.id}")
                            return@forEach
                        }
                        val source = offlineCtx.createBufferSource()
                        source.buffer = buffer
                        val g = offlineCtx.createGain()
                        g.gain.value = trackVolume
                        source.connect(g)
                        g.connect(masterGain)
                        
                        val trimStartSeconds = (item.trimStartBeats / bps)
                        source.start(startSeconds, trimStartSeconds, item.duration / bps)
                        scheduledCount++
                    }
                }
            }
        }

        println("RENDER: Scheduled $scheduledCount audio events. Rendering...")

        return try {
            val result = offlineCtx.startRendering().await()
            println("RENDER: Rendering complete. Buffer duration: ${result.duration}s")
            result
        } catch (e: Exception) {
            println("RENDER: Error during offline rendering: $e")
            null
        }
    }

    actual fun playSingleBuffer(buffer: Any, offsetMillis: Int, onCompletion: () -> Unit) {
        stopPlayback()
        ensureContextResumed()
        
        val audioBuffer = buffer as AudioBuffer
        val source = audioContext.createBufferSource()
        source.buffer = audioBuffer
        source.connect(masterGain)
        
        source.onended = {
            if (currentSource == source) currentSource = null
            source.disconnect()
            onCompletion()
        }
        
        val offsetSeconds = offsetMillis / 1000.0
        if (offsetSeconds < audioBuffer.duration) {
            source.start(0.0, offsetSeconds)
            currentSource = source
        } else {
            onCompletion()
        }
    }
}

